import { useState } from 'react';
import type { FormEvent } from 'react';
import { AlertCircle, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from './core/Button';
import { useAuth } from '../graphql/hooks/useAuth';
import enttroLogo from '../assets/logo.png'
import { DEFAULT_ENDPOINT_URL_EXPORT } from '../graphql/client';

export function LoginPage() {
  const { signIn } = useAuth(); 
  const [isLoading, setIsLoading] = useState(false); 
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Bitte füllen Sie alle Felder aus');
      return;
    }

    try {
      setIsLoading(true);

      const success = await signIn(email, password);

      if (!success) {
        setError('Anmeldung fehlgeschlagen');
        return;
      }


    } catch (err) {
      setError(err instanceof Error ? err.message : 'Anmeldung fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e: FormEvent) => {
    e.preventDefault()

    try {
      setResetLoading(true)

      const response = await fetch(
        `${DEFAULT_ENDPOINT_URL_EXPORT}/auth/forgot-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: resetEmail }),
        }
      )

      const data = await response.json()

      if (!response.ok) {
        if (data.message === 'passwords.user') {
          setResetError('Diese E-Mail-Adresse existiert nicht.')
        } else {
          setResetError('Fehler beim Senden des Reset-Links.')
        }
        return
      }

      setResetSent(true)

    } catch (err) {
      setResetError('Serverfehler. Bitte versuchen Sie es später erneut.')
    } finally {
      setResetLoading(false)
    }
  }

  const closeForgotModal = () => {
    setShowForgotModal(false)
    setResetEmail('')
    setResetError('')
    setResetSent(false)
  }

  const isFormValid = email.trim() !== '' && password.trim() !== '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a2540] via-[#0d3a5f] to-[#0a2540] flex items-center justify-center p-4 md:p-8">
      <div className="w-full max-w-[820px]">
        <div className="text-center mb-8">
          <div className="inline-block">
            <div className="flex items-center justify-center mb-4">
              <img 
                src={enttroLogo} 
                alt="ENTRO" 
                className="h-16 w-auto"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-row gap-6">
          <div className="flex-1 bg-background rounded-[16px] shadow-2xl p-8 md:p-10 border border-gray-100">
                <div className="mb-8">

                  <h2 className="text-[#1c1c1c] text-[24px] font-medium mb-2">
                    Anmelden
                  </h2>
                  <p className="text-[#6b6b6b] text-[14px]">
                    Bitte melden Sie sich mit Ihren Zugangsdaten an
                  </p>
                </div>

                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-[10px] flex items-start gap-3">
                    <AlertCircle className="size-5 text-red-600 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-red-700 text-[14px] font-medium mb-1">
                        Anmeldung fehlgeschlagen
                      </p>
                      <p className="text-red-600 text-[13px]">
                        {error}
                      </p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label 
                      htmlFor="email" 
                      className="block text-[#1c1c1c] text-[14px] font-medium mb-2"
                    >
                      Username oder Email
                    </label>
                    <input
                      id="email"
                      type="text"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="username"
                      disabled={isLoading}
                      className="w-full box-border px-4 py-3 border border-[#e9e9e9] rounded-[10px] text-[#1c1c1c] text-[15px] placeholder:text-[#6b6b6b] focus:outline-none focus:ring-2 focus:ring-[#0084c7] focus:border-transparent transition-all disabled:bg-[#f5f5f5] disabled:cursor-not-allowed"
                      autoComplete="email"
                      autoFocus
                    />
                  </div>

                  <div>
                    <label 
                      htmlFor="password" 
                      className="block text-[#1c1c1c] text-[14px] font-medium mb-2"
                    >
                      Passwort
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        disabled={isLoading}
                        className="w-full box-border px-4 py-3 pr-12 border border-[#e9e9e9] rounded-[10px] text-[#1c1c1c] text-[15px] placeholder:text-[#6b6b6b] focus:outline-none focus:ring-2 focus:ring-[#0084c7] focus:border-transparent transition-all disabled:bg-[#f5f5f5] disabled:cursor-not-allowed"
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={isLoading}
                        className="absolute bg-transparent border-0 outline-none focus:outline-none focus-visible:outline-none right-3 top-1/2 -translate-y-1/2 p-1.5 text-[#6b6b6b] hover:text-[#1c1c1c] transition-colors disabled:cursor-not-allowed"
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <EyeOff className="size-5" />
                        ) : (
                          <Eye className="size-5" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowForgotModal(true)}
                      className="text-[13px] text-[#0084c7] hover:underline"
                    >
                      Passwort vergessen?
                    </button>
                  </div>

                  <div className="pt-2">
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      fullWidth
                      disabled={!isFormValid || isLoading}
                      icon={isLoading ? <Loader2 className="size-5 animate-spin" /> : undefined}
                    >
                      {isLoading ? 'Wird angemeldet...' : 'Anmelden'}
                    </Button>
                  </div>
                </form>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center my-4">
          <p className="text-[#9ca3af] text-[12px]">
            © 2026 ENT-TRO GmbH by{' '}
            <a
              href="https://curvedesign.de"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-inherit"
            >
              CurveDesign GmbH
            </a>
          </p>
        </div>
      </div>
      {showForgotModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-[14px] w-full max-w-[420px] p-8 shadow-xl">

            {!resetSent ? (
              <>
                <h3 className="text-[20px] font-medium mb-2">
                  Passwort zurücksetzen
                </h3>

                <p className="text-[#6b6b6b] text-[14px] mb-6">
                  Geben Sie Ihre E-Mail-Adresse ein. Wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts.
                </p>

                <form onSubmit={handlePasswordReset} className="space-y-4">
                  {resetError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-[8px] text-red-700 text-[13px]">
                      {resetError}
                    </div>
                  )}
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="E-Mail"
                    className="w-full box-border px-4 py-3 border border-[#e9e9e9] rounded-[10px]"
                  />

                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    disabled={!resetEmail || resetLoading}
                    icon={resetLoading ? <Loader2 className="size-5 animate-spin" /> : undefined}
                  >
                    {resetLoading ? 'Wird gesendet...' : 'Reset-Link senden'}
                  </Button>

                  <button
                    type="button"
                    onClick={closeForgotModal}
                    className="w-full text-[13px] text-[#6b6b6b] hover:underline"
                  >
                    Abbrechen
                  </button>

                </form>
              </>
            ) : (
              <>
                <h3 className="text-[20px] font-medium mb-2">
                  E-Mail gesendet
                </h3>

                <p className="text-[#6b6b6b] text-[14px] mb-6">
                  Bitte prüfen Sie Ihr Postfach. Wir haben Ihnen einen Link zum Zurücksetzen Ihres Passworts gesendet.
                </p>

                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  onClick={() => {
                    setShowForgotModal(false);
                    setResetSent(false);
                    setResetEmail('');
                  }}
                >
                  Schließen
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}